<template>
  <div class="p-6">
    <!-- 页面标题和操作按钮 -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">套餐管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理会员套餐和功能配置</p>
      </div>
      <button @click="handleCreatePlan"
        class="primary-btn gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        创建套餐
      </button>
    </div>

    <!-- 套餐列表 -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <PlanList :plans="plans" :categories="categories" @edit="handleEditPlan" @delete="handleDeletePlan"
        @viewPricings="handleViewPricings" @viewFeatures="handleViewFeatures" @syncQuotas="handleSyncQuotas"
        @toggle-hidden="handleTogglePlanHidden" />
    </div>

    <!-- 创建/编辑套餐 Modal -->
    <Modal :show="showPlanModal" @close="closePlanModal" title="套餐信息">
      <PlanForm :plan="currentPlan" :categories="categories" :isEdit="isEditMode" @submit="handlePlanSubmit"
        @cancel="closePlanModal" />
    </Modal>

    <!-- 价格方案管理 Modal -->
    <Modal :show="showPricingsModal" @close="closePricingsModal" :title="`${displayLocalizedText(currentPlan?.name) || ''} - 价格方案`"
      size="large">
      <div class="space-y-6">
        <!-- 添加价格方案按钮和显示隐藏项开关 -->
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-4">
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">价格方案列表</h3>
            <label class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
              <input v-model="includeHiddenPricings" type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded" />
              <span>显示隐藏项</span>
            </label>
          </div>
          <button @click="handleAddPricing"
            class="primary-btn text-sm gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            添加价格方案
          </button>
        </div>

        <!-- 价格方案列表 -->
        <PricingList :pricings="pricings" @edit="handleEditPricing" @delete="handleDeletePricing"
          @toggle-hidden="handleTogglePricingHidden" />
      </div>
    </Modal>

    <!-- 创建/编辑价格方案 Modal -->
    <Modal :show="showPricingFormModal" @close="closePricingFormModal" title="价格方案信息">
      <PricingForm :pricing="currentPricing" :planId="currentPlan?.id" :isEdit="isPricingEditMode"
        @submit="handlePricingSubmit" @cancel="closePricingFormModal" />
    </Modal>

    <!-- 套餐功能配置 Modal -->
    <Modal :show="showFeaturesModal" @close="closeFeaturesModal" :title="`${displayLocalizedText(currentPlan?.name) || ''} - 功能配置`"
      size="large">
      <div class="space-y-6">
        <!-- 添加功能按钮 -->
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">已配置功能</h3>
          <button @click="handleAddFeature"
            class="primary-btn text-sm gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            添加功能
          </button>
        </div>

        <!-- 功能列表 -->
        <PlanFeatureList :planFeatures="planFeatures" @edit="handleEditPlanFeature" @remove="handleRemovePlanFeature" />
      </div>
    </Modal>

    <!-- 添加/编辑套餐功能 Modal -->
    <Modal :show="showPlanFeatureModal" @close="closePlanFeatureModal" title="配置功能">
      <PlanFeatureForm :planFeature="currentPlanFeature" :availableFeatures="availableFeatures"
        :isEdit="isEditPlanFeatureMode" @submit="handlePlanFeatureSubmit" @cancel="closePlanFeatureModal" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import PlanList from '@/components/membership/PlanList.vue'
import PlanForm from '@/components/membership/PlanForm.vue'
import PlanFeatureList from '@/components/membership/PlanFeatureList.vue'
import PlanFeatureForm from '@/components/membership/PlanFeatureForm.vue'
import PricingList from '@/components/membership/PricingList.vue'
import PricingForm from '@/components/membership/PricingForm.vue'
import Modal from '@/components/common/Modal.vue'
import { getAllPlansAPI, createPlanAPI, updatePlanAPI, deletePlanAPI, syncPlanQuotasAPI } from '@/api/membership/plan'
import { getAllFeaturesAPI, getPlanFeaturesAPI, addPlanFeatureAPI, updatePlanFeatureAPI, removePlanFeatureAPI } from '@/api/membership/feature'
import { getAllCategoriesAPI } from '@/api/membership/category'
import { getPlanPricingsAPI, createPricingAPI, updatePricingAPI, deletePricingAPI } from '@/api/membership/pricing'
import type { Plan, CreatePlanRequest, UpdatePlanRequest } from '@/types/membership/plan'
import type { Feature, PlanFeature, CreatePlanFeatureRequest, UpdatePlanFeatureRequest } from '@/types/membership/feature'
import type { PlanCategory } from '@/types/membership/category'
import type { PlanPricing, CreatePricingRequest, UpdatePricingRequest } from '@/types/membership/pricing'
import { displayLocalizedText } from '@/types/membership/localized'

/**
 * 套餐列表
 */
const plans = ref<Plan[]>([])

/**
 * 套餐分类列表
 */
const categories = ref<PlanCategory[]>([])

/**
 * 功能列表
 */
const features = ref<Feature[]>([])

/**
 * 当前套餐的功能列表
 */
const planFeatures = ref<PlanFeature[]>([])

/**
 * 当前操作的套餐
 */
const currentPlan = ref<Plan | undefined>(undefined)

/**
 * 当前操作的套餐功能
 */
const currentPlanFeature = ref<PlanFeature | undefined>(undefined)

/**
 * 价格方案列表
 */
const pricings = ref<PlanPricing[]>([])

/**
 * 当前操作的价格方案
 */
const currentPricing = ref<PlanPricing | undefined>(undefined)

/**
 * 是否包含隐藏的价格方案
 */
const includeHiddenPricings = ref(true)

/**
 * 显示套餐 Modal
 */
const showPlanModal = ref(false)

/**
 * 显示价格方案列表 Modal
 */
const showPricingsModal = ref(false)

/**
 * 显示价格方案表单 Modal
 */
const showPricingFormModal = ref(false)

/**
 * 显示功能配置 Modal
 */
const showFeaturesModal = ref(false)

/**
 * 显示套餐功能 Modal
 */
const showPlanFeatureModal = ref(false)

/**
 * 是否为编辑模式
 */
const isEditMode = ref(false)

/**
 * 是否为编辑价格方案模式
 */
const isPricingEditMode = ref(false)

/**
 * 是否为编辑套餐功能模式
 */
const isEditPlanFeatureMode = ref(false)

/**
 * 可用功能列表（排除已添加的功能）
 */
const availableFeatures = ref<Feature[]>([])

/**
 * 加载所有套餐
 */
const loadPlans = async () => {
  try {
    const response = await getAllPlansAPI()
    plans.value = response.data.plans
  } catch (error) {
    console.error('加载套餐失败:', error)
  }
}

/**
 * 加载所有套餐分类
 */
const loadCategories = async () => {
  try {
    const response = await getAllCategoriesAPI()
    categories.value = response.data.categories
  } catch (error) {
    console.error('加载套餐分类失败:', error)
  }
}

/**
 * 加载所有功能
 */
const loadFeatures = async () => {
  try {
    const response = await getAllFeaturesAPI()
    features.value = response.data.features
  } catch (error) {
    console.error('加载功能失败:', error)
  }
}

/**
 * 加载套餐功能
 */
const loadPlanFeatures = async (planId: number) => {
  try {
    const response = await getPlanFeaturesAPI(planId)
    planFeatures.value = response.data.planFeatures
    updateAvailableFeatures()
  } catch (error) {
    console.error('加载套餐功能失败:', error)
  }
}

/**
 * 更新可用功能列表
 */
const updateAvailableFeatures = () => {
  const usedFeatureIds = planFeatures.value.map((pf) => pf.featureId)
  availableFeatures.value = features.value.filter((f) => !usedFeatureIds.includes(f.id))
}

/**
 * 创建套餐
 */
const handleCreatePlan = () => {
  currentPlan.value = undefined
  isEditMode.value = false
  showPlanModal.value = true
}

/**
 * 编辑套餐
 */
const handleEditPlan = (plan: Plan) => {
  currentPlan.value = plan
  isEditMode.value = true
  showPlanModal.value = true
}

/**
 * 删除套餐
 */
const handleDeletePlan = async (plan: Plan) => {
  if (!confirm(`确定要删除套餐"${displayLocalizedText(plan.name)}"吗？`)) {
    return
  }
  try {
    await deletePlanAPI(plan.id)
    await loadPlans()
  } catch (error) {
    console.error('删除套餐失败:', error)
  }
}

/**
 * 同步套餐配额到现有用户
 */
const handleSyncQuotas = async (plan: Plan) => {
  if (!confirm(`确定要将套餐「${displayLocalizedText(plan.name)}」的最新配额同步到所有活跃订阅用户吗？\n\n此操作将更新所有使用该套餐的用户的配额限制（保留已使用次数）。`)) {
    return
  }
  try {
    const response = await syncPlanQuotasAPI(plan.id)
    alert(`配额同步成功！更新了 ${response.data.updatedRecords} 条记录`)
  } catch (error: any) {
    console.error('同步配额失败:', error)
    alert(error.message || '同步配额失败')
  }
}

/**
 * 切换套餐隐藏状态
 */
const handleTogglePlanHidden = async (plan: Plan) => {
  try {
    await updatePlanAPI(plan.id, {
      isHidden: !plan.isHidden,
    })
    await loadPlans()
  } catch (error) {
    console.error('切换隐藏状态失败:', error)
  }
}

/**
 * 查看价格方案
 */
const handleViewPricings = async (plan: Plan) => {
  currentPlan.value = plan
  await loadPricings(plan.id)
  showPricingsModal.value = true
}

/**
 * 加载价格方案列表
 */
const loadPricings = async (planId: number) => {
  try {
    const response = await getPlanPricingsAPI(planId, includeHiddenPricings.value)
    pricings.value = response.data.pricings
  } catch (error) {
    console.error('加载价格方案失败:', error)
  }
}

/**
 * 监听 includeHiddenPricings 变化，重新加载价格方案
 */
watch(includeHiddenPricings, () => {
  if (currentPlan.value) {
    loadPricings(currentPlan.value.id)
  }
})

/**
 * 关闭价格方案列表 Modal
 */
const closePricingsModal = () => {
  showPricingsModal.value = false
  currentPlan.value = undefined
  pricings.value = []
}

/**
 * 添加价格方案
 */
const handleAddPricing = () => {
  currentPricing.value = undefined
  isPricingEditMode.value = false
  showPricingFormModal.value = true
}

/**
 * 编辑价格方案
 */
const handleEditPricing = (pricing: PlanPricing) => {
  currentPricing.value = pricing
  isPricingEditMode.value = true
  showPricingFormModal.value = true
}

/**
 * 删除价格方案
 */
const handleDeletePricing = async (pricing: PlanPricing) => {
  if (!confirm(`确定要删除该价格方案吗？`)) {
    return
  }
  try {
    await deletePricingAPI(pricing.id)
    if (currentPlan.value) {
      await loadPricings(currentPlan.value.id)
    }
  } catch (error) {
    console.error('删除价格方案失败:', error)
  }
}

/**
 * 切换价格方案隐藏状态
 */
const handleTogglePricingHidden = async (pricing: PlanPricing) => {
  if (!currentPlan.value) return

  try {
    await updatePricingAPI(pricing.id, {
      isHidden: !pricing.isHidden,
    })
    await loadPricings(currentPlan.value.id)
  } catch (error) {
    console.error('切换隐藏状态失败:', error)
  }
}

/**
 * 提交价格方案表单
 */
const handlePricingSubmit = async (data: CreatePricingRequest | UpdatePricingRequest) => {
  if (!currentPlan.value) {
    console.error('当前套餐不存在')
    return
  }

  try {
    if (isPricingEditMode.value && currentPricing.value) {
      await updatePricingAPI(currentPricing.value.id, data as UpdatePricingRequest)
    } else {
      await createPricingAPI(currentPlan.value.id, data as CreatePricingRequest)
    }
    await loadPricings(currentPlan.value.id)
    closePricingFormModal()
  } catch (error) {
    console.error('保存价格方案失败:', error)
  }
}

/**
 * 关闭价格方案表单 Modal
 */
const closePricingFormModal = () => {
  showPricingFormModal.value = false
  currentPricing.value = undefined
  isPricingEditMode.value = false
}

/**
 * 查看套餐功能
 */
const handleViewFeatures = async (plan: Plan) => {
  currentPlan.value = plan
  await loadPlanFeatures(plan.id)
  showFeaturesModal.value = true
}

/**
 * 提交套餐表单
 */
const handlePlanSubmit = async (data: CreatePlanRequest | UpdatePlanRequest) => {
  try {
    if (isEditMode.value && currentPlan.value) {
      await updatePlanAPI(currentPlan.value.id, data as UpdatePlanRequest)
    } else {
      await createPlanAPI(data as CreatePlanRequest)
    }
    await loadPlans()
    closePlanModal()
  } catch (error) {
    console.error('保存套餐失败:', error)
  }
}

/**
 * 关闭套餐 Modal
 */
const closePlanModal = () => {
  showPlanModal.value = false
  currentPlan.value = undefined
}

/**
 * 关闭功能配置 Modal
 */
const closeFeaturesModal = () => {
  showFeaturesModal.value = false
  currentPlan.value = undefined
  planFeatures.value = []
}

/**
 * 添加功能
 */
const handleAddFeature = () => {
  currentPlanFeature.value = undefined
  isEditPlanFeatureMode.value = false
  showPlanFeatureModal.value = true
}

/**
 * 编辑套餐功能
 */
const handleEditPlanFeature = (planFeature: PlanFeature) => {
  currentPlanFeature.value = planFeature
  isEditPlanFeatureMode.value = true
  showPlanFeatureModal.value = true
}

/**
 * 删除套餐功能
 */
const handleRemovePlanFeature = async (planFeature: PlanFeature) => {
  if (!confirm(`确定要移除功能"${displayLocalizedText(planFeature.feature.name)}"吗？`)) {
    return
  }
  try {
    if (currentPlan.value) {
      await removePlanFeatureAPI(currentPlan.value.id, planFeature.id)
      await loadPlanFeatures(currentPlan.value.id)
    }
  } catch (error) {
    console.error('删除套餐功能失败:', error)
  }
}

/**
 * 提交套餐功能表单
 */
const handlePlanFeatureSubmit = async (data: CreatePlanFeatureRequest | UpdatePlanFeatureRequest) => {
  try {
    if (currentPlan.value) {
      if (isEditPlanFeatureMode.value && currentPlanFeature.value) {
        await updatePlanFeatureAPI(currentPlan.value.id, currentPlanFeature.value.id, data as UpdatePlanFeatureRequest)
      } else {
        await addPlanFeatureAPI(currentPlan.value.id, data as CreatePlanFeatureRequest)
      }
      await loadPlanFeatures(currentPlan.value.id)
      closePlanFeatureModal()
    }
  } catch (error) {
    console.error('保存套餐功能失败:', error)
  }
}

/**
 * 关闭套餐功能 Modal
 */
const closePlanFeatureModal = () => {
  showPlanFeatureModal.value = false
  currentPlanFeature.value = undefined
}

/**
 * 组件挂载时加载数据
 */
onMounted(async () => {
  await Promise.all([
    loadPlans(),
    loadCategories(),
    loadFeatures()
  ])
})
</script>
